import { Component, ErrorInfo, ReactNode } from 'react'
import { trackCustomError } from '@/utils/newrelic'

const isDev = process.env.NODE_ENV === 'development'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
}

export class DevErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo })

    // Enhanced error logging in development
    if (isDev) {
      console.group('🚨 React Error Boundary')
      console.error('Error:', error)
      console.error('Error Info:', errorInfo)
      console.error('Component Stack:', errorInfo.componentStack)
      console.groupEnd()
    }

    // Track error with New Relic in production
    if (!isDev) {
      trackCustomError(error, {
        componentStack: errorInfo.componentStack,
        errorBoundary: 'DevErrorBoundary',
        timestamp: Date.now(),
      })
    }
  }

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI in development
      if (isDev && this.state.error) {
        return (
          <div
            style={{
              padding: '20px',
              margin: '20px',
              border: '2px solid #ff6b6b',
              borderRadius: '8px',
              backgroundColor: '#fff5f5',
              fontFamily: 'monospace',
            }}
          >
            <h2 style={{ color: '#c92a2a', margin: '0 0 16px 0' }}>🚨 Component Error</h2>
            <details>
              <summary style={{ cursor: 'pointer', marginBottom: '12px' }}>
                <strong>Error Details</strong>
              </summary>
              <pre
                style={{
                  backgroundColor: '#f8f9fa',
                  padding: '12px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  fontSize: '12px',
                }}
              >
                {this.state.error.toString()}
              </pre>
            </details>

            {this.state.errorInfo && (
              <details>
                <summary style={{ cursor: 'pointer', marginBottom: '12px' }}>
                  <strong>Component Stack</strong>
                </summary>
                <pre
                  style={{
                    backgroundColor: '#f8f9fa',
                    padding: '12px',
                    borderRadius: '4px',
                    overflow: 'auto',
                    fontSize: '12px',
                  }}
                >
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <button
              onClick={() =>
                this.setState({ hasError: false, error: undefined, errorInfo: undefined })
              }
              style={{
                marginTop: '16px',
                padding: '8px 16px',
                backgroundColor: '#228be6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              🔄 Retry Component
            </button>
          </div>
        )
      }

      // Production fallback or custom fallback
      return (
        this.props.fallback || (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <h3>Something went wrong</h3>
            <p>Please refresh the page or contact support if the problem persists.</p>
          </div>
        )
      )
    }

    return this.props.children
  }
}
